import React, { useState } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useRatingWorkflow } from "../../../../context/RatingWorkflowContext";
import type { KpiCategory } from "newui/features/MySuppliers/types/supplier-performance.types";

interface SelectCategoriesStepProps {
	categories: KpiCategory[];
	isLoading: boolean;
	supplierProfileType?: string;
}

export const SelectCategoriesStep: React.FC<SelectCategoriesStepProps> = ({
	categories,
	isLoading,
	supplierProfileType,
}) => {
	const { state, dispatch } = useRatingWorkflow();
	const [expandedCategories, setExpandedCategories] = useState<Set<number>>(
		new Set()
	);

	const handleToggleCategory = (categoryId: number) => {
		const currentIds = state.selectedCategoryIds;
		const isSelected = currentIds.includes(categoryId);

		dispatch({
			type: "SET_CATEGORIES",
			payload: {
				categoryIds: isSelected
					? currentIds.filter((id) => id !== categoryId)
					: [...currentIds, categoryId],
			},
		});
	};

	const handleToggleExpand = (categoryId: number) => {
		setExpandedCategories((prev) => {
			const next = new Set(prev);
			if (next.has(categoryId)) {
				next.delete(categoryId);
			} else {
				next.add(categoryId);
			}
			return next;
		});
	};

	const handleQuickStart = () => {
		dispatch({
			type: "SET_CATEGORIES",
			payload: { categoryIds: categories.map((cat) => cat.id) },
		});
	};

	const handleClear = () => {
		dispatch({
			type: "SET_CATEGORIES",
			payload: { categoryIds: [] },
		});
	}

	return (
		<div data-testid="select-categories-step">
			<div className="mb-4 flex items-center justify-between">
				<div>
					<h3 className="text-lg font-semibold text-gray-900">
						Select KPI Categories
					</h3>
					<p className="text-sm text-gray-600">
						Choose which categories to evaluate. Select at least one.
					</p>
				</div>
				<div className="flex gap-4">
					<button
						type="button"
						onClick={handleQuickStart}
						className="rounded-md bg-blue-100 px-3 py-1.5 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-200"
						data-testid="quick-start-button"
					>
						Quick Start
					</button>
					<button
						type="button"
						onClick={handleClear}
						className="rounded-md border border-blue-700 px-3 py-1.5 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-200"
						data-testid="quick-start-button"
					>
						Clear Selecton
					</button>
				</div>
			</div>

			{isLoading && (
				<div className="py-8 text-center text-sm text-gray-500">
					Loading categories...
				</div>
			)}

			{!isLoading && (
				<div className="space-y-2">
					{categories.map((category) => {
						const isSelected =
							state.selectedCategoryIds.includes(category.id);
						const isExpanded = expandedCategories.has(category.id);

						return (
							<div
								key={category.id}
								className={`rounded-lg border transition-colors ${isSelected
									? "border-blue-300 bg-blue-50"
									: "border-gray-200"
									}`}
								data-testid={`category-${category.id}`}
							>
								<div className="flex items-center gap-3 p-3">
									<input
										type="checkbox"
										checked={isSelected}
										onChange={() =>
											handleToggleCategory(category.id)
										}
										className="h-4 w-4 rounded border-gray-300 text-blue-600"
										data-testid={`category-checkbox-${category.id}`}
									/>
									<div className="flex-1">
										<div className="text-sm font-medium text-gray-900">
											{category.name}
										</div>
									</div>
									<button
										type="button"
										onClick={() =>
											handleToggleExpand(category.id)
										}
										className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
										aria-label={
											isExpanded
												? `Collapse ${category.name}`
												: `Expand ${category.name}`
										}
									>
										{isExpanded ? (
											<FiChevronUp className="h-4 w-4" />
										) : (
											<FiChevronDown className="h-4 w-4" />
										)}
									</button>
								</div>
								{isExpanded && (
									<div className="border-t border-gray-200 px-3 py-2">
										<p className="mb-2 text-xs text-gray-600">
											{category.description}
										</p>
										{category.sub_criteria.length > 0 && (
											<ul className="space-y-1">
												{category.sub_criteria.map(
													(sub) => (
														<li
															key={sub.id}
															className="text-xs text-gray-500"
														>
															• {sub.name}
														</li>
													)
												)}
											</ul>
										)}
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}

			<div className="mt-4 text-sm text-gray-500">
				{state.selectedCategoryIds.length} of {categories.length}{" "}
				categories selected
			</div>
		</div>
	);
};
